import React, { useEffect, useState } from 'react';
import API from '../api';
import './SellerIngestionPanel.css';

/**
 * Pannello self-service per l'integrazione "API Push" (Ingestion API).
 *
 * Funzionalità:
 *  - Attivare la modalità push (setta seller_info.integration_method='push')
 *  - Generare/revocare API key (live/test, scope, label)
 *  - Sottoscrivere/rimuovere webhook
 *  - Vedere stato del catalogo (lag stats da ES)
 *  - Vedere ultimi log richieste
 *
 * Backend: /api/seller/ingestion/* (auth JWT seller).
 */
function SellerIngestionPanel({ sellerInfo, onActivated }) {
  const [tab, setTab] = useState('overview');
  const [keys, setKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [newWebhookResult, setNewWebhookResult] = useState(null);

  const isPushActive = sellerInfo?.integrationMethod === 'push';

  useEffect(() => {
    if (isPushActive) {
      reloadAll();
    } else {
      // Anche pre-attivazione caricamo le chiavi: il vendor può generare e
      // gestire chiavi MODE=TEST senza disattivare l'integrazione esistente.
      fetchKeys();
    }
  }, [isPushActive]);

  const reloadAll = async () => {
    await Promise.all([fetchKeys(), fetchWebhooks(), fetchStats(), fetchLogs()]);
  };

  const fetchKeys = async () => {
    try {
      const { data } = await API.get('/seller/ingestion/keys');
      setKeys(data.items ?? []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchWebhooks = async () => {
    try {
      const { data } = await API.get('/seller/ingestion/webhooks');
      setWebhooks(data.items ?? []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await API.get('/seller/ingestion/lag');
      setStats(data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchLogs = async () => {
    try {
      const { data } = await API.get('/seller/ingestion/logs?limit=50');
      setLogs(data.items ?? []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleActivate = async () => {
    if (!window.confirm(
      'Vuoi attivare la modalità Push API? Il cron di import automatico verrà disabilitato e dovrai inviare i prodotti via API. Puoi sempre tornare indietro impostando un altro metodo.',
    )) return;
    setLoading(true);
    try {
      await API.post('/seller/ingestion/activate');
      setMessage('Push API attivata. Genera ora una chiave API per iniziare.');
      onActivated?.();
      setTab('keys');
    } catch (err) {
      setMessage(err?.response?.data?.message ?? 'Errore attivazione');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const scope = form.getAll('scope');
    const payload = {
      mode: form.get('mode'),
      scope,
      label: form.get('label'),
    };
    setLoading(true);
    setNewKeyResult(null);
    try {
      const { data } = await API.post('/seller/ingestion/keys', payload);
      setNewKeyResult(data);
      e.target.reset();
      await fetchKeys();
    } catch (err) {
      setMessage(err?.response?.data?.message ?? 'Errore creazione chiave');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async (id) => {
    if (!window.confirm('Revoca questa chiave? Le richieste con questa chiave falliranno immediatamente.')) return;
    setLoading(true);
    try {
      await API.delete(`/seller/ingestion/keys/${id}`);
      await fetchKeys();
    } catch (err) {
      setMessage(err?.response?.data?.message ?? 'Errore revoca');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebhook = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const events = form.getAll('events');
    const payload = { url: form.get('url'), events };
    setLoading(true);
    setNewWebhookResult(null);
    try {
      const { data } = await API.post('/seller/ingestion/webhooks', payload);
      setNewWebhookResult(data);
      e.target.reset();
      await fetchWebhooks();
    } catch (err) {
      setMessage(err?.response?.data?.message ?? 'Errore creazione webhook');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWebhook = async (id) => {
    if (!window.confirm('Eliminare questa subscription? Non riceverai più gli eventi.')) return;
    try {
      await API.delete(`/seller/ingestion/webhooks/${id}`);
      await fetchWebhooks();
    } catch (err) {
      setMessage(err?.response?.data?.message ?? 'Errore eliminazione');
    }
  };

  if (!isPushActive) {
    return (
      <div className="ingestion-panel">
        <div className="ingestion-activate-cta">
          <h3>Modalità API Push</h3>
          <p>
            Invia i prodotti a Loverlist tramite chiamate REST direttamente dal tuo sistema.
            Niente cron di import: gli aggiornamenti (prezzo, stock, nuovi prodotti) sono visibili
            quasi in tempo reale.
          </p>
          <ul className="ingestion-bullets">
            <li>Controllo totale: invii solo quello che vuoi, quando vuoi</li>
            <li>Latenza minima: stock e prezzo aggiornati in pochi secondi</li>
            <li>Documentazione interattiva: <a href="/api/ingestion/v1/docs" target="_blank" rel="noreferrer">Swagger UI</a></li>
            <li>Ideale per piattaforme custom, marketplace o team con un dev team</li>
          </ul>
          <p className="ingestion-warning">
            ⚠️ Attivando la Push API disabiliti il cron di import automatico esistente.
            Dovrai gestire le sincronizzazioni dal tuo sistema.
          </p>
          <button
            className="ingestion-btn ingestion-btn-primary"
            onClick={handleActivate}
            disabled={loading}
          >
            {loading ? 'Attivazione...' : 'Attiva modalità API Push'}
          </button>
          {message && <div className="ingestion-msg">{message}<button onClick={() => setMessage('')}>×</button></div>}
        </div>

        {/* Gestione chiavi pre-attivazione: il vendor può generare chiavi sia
            in modalità `test` (sandbox isolato) sia `live` (indici reali) anche
            mentre l'integrazione legacy resta attiva. Le scritture API-pushed
            sono protette dalle operazioni distruttive del cron pull tramite
            `connector_type='ingestion_api'` — vedi `services/_ingestionProtection.js`.
            Coesistenza Shopify/Woo/etc + API push è quindi sicura. */}
        <div className="ingestion-section" style={{ marginTop: 32 }}>
          <h3>Chiavi API</h3>
          <p>
            Puoi generare chiavi anche prima di attivare formalmente la modalità Push.
            La modalità <strong>live</strong> scrive sugli indici reali (i prodotti compaiono
            sul sito), la <strong>test</strong> scrive su indici sandbox isolati. Entrambe
            convivono senza problemi con l'integrazione attuale: i prodotti pushed via API
            non vengono cancellati dal cron di import del CMS legacy.
          </p>

          {keys.length > 0 && (
            <table className="ingestion-table">
              <thead>
                <tr>
                  <th>Label</th><th>Mode</th><th>Prefix</th><th>Scope</th><th>Creata</th><th>Stato</th><th></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id}>
                    <td>{k.label}</td>
                    <td><span className={`ingestion-badge ingestion-badge-${k.mode}`}>{k.mode}</span></td>
                    <td><code>{k.keyPrefix}…</code></td>
                    <td>{(k.scope ?? []).join(', ')}</td>
                    <td>{new Date(k.createdAt).toLocaleDateString('it-IT')}</td>
                    <td>
                      {k.revokedAt
                        ? <span className="ingestion-badge ingestion-badge-revoked">revocata</span>
                        : <span className="ingestion-badge ingestion-badge-active">attiva</span>}
                    </td>
                    <td>
                      {!k.revokedAt && (
                        <button onClick={() => handleRevokeKey(k.id)} className="ingestion-btn-danger">Revoca</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {newKeyResult && (
            <div className="ingestion-key-result">
              <strong>⚠️ Salva questa chiave subito — non sarà più mostrata</strong>
              <pre className="ingestion-rawkey">{newKeyResult.rawKey}</pre>
              <button onClick={() => navigator.clipboard?.writeText(newKeyResult.rawKey)}>Copia</button>
              <button onClick={() => setNewKeyResult(null)} className="ingestion-btn-secondary">Chiudi</button>
            </div>
          )}

          <h4>Genera nuova chiave</h4>
          <p className="ingestion-hint">
            Le chiavi <code>live</code> scrivono sugli indici reali (visibili sul sito) e
            <strong> coesistono</strong> con l'integrazione attualmente attiva — non viene cancellato
            niente di esistente. Le chiavi <code>test</code> scrivono su indici sandbox isolati,
            utili per validare l'integrazione tecnica.
          </p>
          <form onSubmit={handleCreateKey} className="ingestion-form">
            <label>
              Label
              <input type="text" name="label" required maxLength="255" placeholder="es. Production server" />
            </label>
            <label>
              Mode
              <select name="mode" defaultValue="test">
                <option value="test">test (indici sandbox, invisibili dal sito)</option>
                <option value="live">live (indici reali, visibili dal sito)</option>
              </select>
            </label>
            <fieldset className="ingestion-scope-grid">
              <legend>Permessi (scope)</legend>
              <label><input type="checkbox" name="scope" value="read:products" defaultChecked /> read:products</label>
              <label><input type="checkbox" name="scope" value="write:products" defaultChecked /> write:products</label>
              <label><input type="checkbox" name="scope" value="manage:webhooks" /> manage:webhooks</label>
              <label><input type="checkbox" name="scope" value="read:webhooks" /> read:webhooks</label>
            </fieldset>
            <button type="submit" disabled={loading} className="ingestion-btn ingestion-btn-primary">
              {loading ? 'Generazione…' : 'Genera chiave'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="ingestion-panel">
      <div className="ingestion-tabs">
        <button className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>Panoramica</button>
        <button className={tab === 'keys' ? 'active' : ''} onClick={() => setTab('keys')}>API Keys</button>
        <button className={tab === 'webhooks' ? 'active' : ''} onClick={() => setTab('webhooks')}>Webhooks</button>
        <button className={tab === 'logs' ? 'active' : ''} onClick={() => setTab('logs')}>Log richieste</button>
      </div>

      {message && <div className="ingestion-msg">{message}<button onClick={() => setMessage('')}>×</button></div>}

      {tab === 'overview' && (
        <div className="ingestion-overview">
          <div className="ingestion-card">
            <h4>Catalogo</h4>
            <div className="ingestion-stat-row">
              <span>Prodotti indicizzati</span>
              <strong>{stats?.totalProducts ?? '—'}</strong>
            </div>
            <div className="ingestion-stat-row">
              <span>Ultimo aggiornamento</span>
              <strong>{stats?.lastIndexedAt ? new Date(stats.lastIndexedAt).toLocaleString('it-IT') : '—'}</strong>
            </div>
            {stats?.availability && (
              <div className="ingestion-stat-row">
                <span>Disponibilità</span>
                <strong>
                  {Object.entries(stats.availability).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}
                </strong>
              </div>
            )}
          </div>
          <div className="ingestion-card">
            <h4>Documentazione</h4>
            <p>
              Endpoint base: <code>https://loverlist.com/api/ingestion/v1</code>
            </p>
            <p>
              <a href="/api/ingestion/v1/docs" target="_blank" rel="noreferrer">Swagger UI interattiva →</a>
            </p>
            <p>
              <a href="/api/ingestion/v1/openapi.json" target="_blank" rel="noreferrer">Scarica OpenAPI 3.1 spec</a>
            </p>
          </div>
        </div>
      )}

      {tab === 'keys' && (
        <div className="ingestion-section">
          <h4>Chiavi API esistenti</h4>
          {keys.length === 0 ? (
            <p className="ingestion-empty">Nessuna chiave creata. Genera la prima per iniziare.</p>
          ) : (
            <table className="ingestion-table">
              <thead>
                <tr>
                  <th>Label</th><th>Mode</th><th>Prefix</th><th>Scope</th><th>Creata</th><th>Ultimo uso</th><th>Stato</th><th></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id}>
                    <td>{k.label}</td>
                    <td><span className={`ingestion-badge ingestion-badge-${k.mode}`}>{k.mode}</span></td>
                    <td><code>{k.keyPrefix}…</code></td>
                    <td>{(k.scope ?? []).join(', ')}</td>
                    <td>{new Date(k.createdAt).toLocaleDateString('it-IT')}</td>
                    <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString('it-IT') : '—'}</td>
                    <td>
                      {k.revokedAt
                        ? <span className="ingestion-badge ingestion-badge-revoked">revocata</span>
                        : <span className="ingestion-badge ingestion-badge-active">attiva</span>}
                    </td>
                    <td>
                      {!k.revokedAt && (
                        <button onClick={() => handleRevokeKey(k.id)} className="ingestion-btn-danger">Revoca</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {newKeyResult && (
            <div className="ingestion-key-result">
              <strong>⚠️ Salva questa chiave subito — non sarà più mostrata</strong>
              <pre className="ingestion-rawkey">{newKeyResult.rawKey}</pre>
              <button onClick={() => navigator.clipboard?.writeText(newKeyResult.rawKey)}>Copia</button>
              <button onClick={() => setNewKeyResult(null)} className="ingestion-btn-secondary">Chiudi</button>
            </div>
          )}

          <h4>Genera nuova chiave</h4>
          <form onSubmit={handleCreateKey} className="ingestion-form">
            <label>
              Label
              <input type="text" name="label" required maxLength="255" placeholder="es. Server di produzione" />
            </label>
            <label>
              Mode
              <select name="mode" defaultValue="test">
                <option value="test">test (scrive su indici sandbox)</option>
                <option value="live">live (scrive sugli indici reali)</option>
              </select>
            </label>
            <fieldset className="ingestion-scope-grid">
              <legend>Permessi (scope)</legend>
              <label><input type="checkbox" name="scope" value="read:products" defaultChecked /> read:products</label>
              <label><input type="checkbox" name="scope" value="write:products" defaultChecked /> write:products</label>
              <label><input type="checkbox" name="scope" value="manage:webhooks" /> manage:webhooks</label>
              <label><input type="checkbox" name="scope" value="read:webhooks" /> read:webhooks</label>
            </fieldset>
            <button type="submit" disabled={loading} className="ingestion-btn ingestion-btn-primary">
              {loading ? 'Generazione…' : 'Genera chiave'}
            </button>
          </form>
        </div>
      )}

      {tab === 'webhooks' && (
        <div className="ingestion-section">
          <h4>Webhook configurati</h4>
          {webhooks.length === 0 ? (
            <p className="ingestion-empty">Nessun webhook. Sottoscrivi gli eventi per ricevere notifiche.</p>
          ) : (
            <table className="ingestion-table">
              <thead>
                <tr>
                  <th>URL</th><th>Eventi</th><th>Stato</th><th>Creato</th><th></th>
                </tr>
              </thead>
              <tbody>
                {webhooks.map((w) => (
                  <tr key={w.id}>
                    <td><code>{w.url}</code></td>
                    <td>{(w.events ?? []).join(', ')}</td>
                    <td>{w.active ? 'attivo' : 'disabilitato'}</td>
                    <td>{new Date(w.createdAt).toLocaleDateString('it-IT')}</td>
                    <td>
                      {w.active && (
                        <button onClick={() => handleDeleteWebhook(w.id)} className="ingestion-btn-danger">Rimuovi</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {newWebhookResult && (
            <div className="ingestion-key-result">
              <strong>⚠️ Signing secret — salvalo subito, ti serve per verificare le firme HMAC</strong>
              <pre className="ingestion-rawkey">{newWebhookResult.signing_secret ?? newWebhookResult.signingSecret}</pre>
              <button onClick={() => setNewWebhookResult(null)} className="ingestion-btn-secondary">Chiudi</button>
            </div>
          )}

          <h4>Sottoscrivi nuovo webhook</h4>
          <form onSubmit={handleCreateWebhook} className="ingestion-form">
            <label>
              URL (https obbligatorio)
              <input type="url" name="url" required pattern="https://.*" maxLength="2048" placeholder="https://example.com/webhooks/loverlist" />
            </label>
            <fieldset className="ingestion-scope-grid">
              <legend>Eventi da ricevere</legend>
              <label><input type="checkbox" name="events" value="product.indexed" defaultChecked /> product.indexed</label>
              <label><input type="checkbox" name="events" value="product.rejected" defaultChecked /> product.rejected</label>
              <label><input type="checkbox" name="events" value="product.expired" /> product.expired</label>
            </fieldset>
            <button type="submit" disabled={loading} className="ingestion-btn ingestion-btn-primary">
              {loading ? 'Creazione…' : 'Sottoscrivi'}
            </button>
          </form>
        </div>
      )}

      {tab === 'logs' && (
        <div className="ingestion-section">
          <h4>Ultime 50 richieste</h4>
          {logs.length === 0 ? (
            <p className="ingestion-empty">Nessuna richiesta ancora.</p>
          ) : (
            <table className="ingestion-table ingestion-table-compact">
              <thead>
                <tr>
                  <th>Quando</th><th>Method</th><th>Path</th><th>Status</th><th>ms</th><th>Error</th><th>Request ID</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.request_id} className={l.status >= 400 ? 'ingestion-row-error' : ''}>
                    <td>{new Date(l.created_at).toLocaleString('it-IT')}</td>
                    <td>{l.method}</td>
                    <td><code>{l.path}</code></td>
                    <td>{l.status}</td>
                    <td>{l.duration_ms}</td>
                    <td>{l.error_code ?? '—'}</td>
                    <td><code>{l.request_id?.slice(0, 8)}…</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <button onClick={fetchLogs} className="ingestion-btn-secondary">Aggiorna</button>
        </div>
      )}
    </div>
  );
}

export default SellerIngestionPanel;
